import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Send, ThumbsUp, ThumbsDown } from 'lucide-react';
import { api, ApiError } from '../api/client';
import { toast } from 'sonner';
import type { FeedPostDto } from '@bstm/shared';

function formatTs(ts: number): string {
  const d = new Date(ts * 1000);
  const now = Date.now();
  const diffSec = Math.floor((now - d.getTime()) / 1000);
  if (diffSec < 60) return 'just now';
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
  return d.toLocaleString();
}

function authorLabel(p: FeedPostDto): string {
  if (p.authorName) return p.authorName;
  if (p.authorPhone) return `…${p.authorPhone.slice(-4)}`;
  return 'Someone';
}

export default function TicketFeed({ ticketId }: { ticketId: string }) {
  const qc = useQueryClient();
  const [draft, setDraft] = useState('');
  const listRef = useRef<HTMLDivElement>(null);
  const prevCountRef = useRef(0);

  const q = useQuery<{ posts: FeedPostDto[] }>({
    queryKey: ['ticket', ticketId, 'feed'],
    queryFn: () => api(`/tickets/${ticketId}/feed`),
  });

  const posts = q.data?.posts ?? [];

  const create = useMutation({
    mutationFn: (body: string) =>
      api<{ post: FeedPostDto }>(`/tickets/${ticketId}/feed`, {
        method: 'POST',
        json: { body },
      }),
    onSuccess: () => {
      setDraft('');
      qc.invalidateQueries({ queryKey: ['ticket', ticketId, 'feed'] });
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Failed to post'),
  });

  useEffect(() => {
    if (posts.length > prevCountRef.current && listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
    prevCountRef.current = posts.length;
  }, [posts.length]);

  function submit() {
    const body = draft.trim();
    if (!body) return;
    create.mutate(body);
  }

  return (
    <div className="pt-4 border-t border-zinc-800">
      <div className="text-sm text-zinc-400 mb-2">Discussion</div>
      <div
        ref={listRef}
        className="space-y-3 max-h-80 overflow-y-auto pr-1"
      >
        {posts.length === 0 && (
          <div className="text-sm text-zinc-500 italic">No posts yet. Start the discussion.</div>
        )}
        {posts.map((p) => (
          <div key={p.id} className="flex flex-col gap-0.5">
            <div className="flex items-baseline gap-2 text-xs text-zinc-500">
              {p.kind === 'VOTE' ? (
                p.body.includes('👎') && !p.body.includes('👍 →') ? (
                  <ThumbsDown size={12} className="text-red-400" />
                ) : (
                  <ThumbsUp size={12} className="text-emerald-400" />
                )
              ) : null}
              <span className="text-zinc-300 font-medium">{authorLabel(p)}</span>
              <span title={new Date(p.createdAt * 1000).toLocaleString()}>
                {formatTs(p.createdAt)}
              </span>
            </div>
            <div
              className={`whitespace-pre-wrap text-sm ${
                p.kind === 'VOTE' ? 'text-zinc-300 italic' : 'text-zinc-100'
              }`}
            >
              {p.body}
            </div>
          </div>
        ))}
      </div>

      <div className="flex items-end gap-2 mt-3">
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          rows={2}
          className="flex-1"
          placeholder="Post an update — fills, slippage, follow-ups…"
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              submit();
            }
          }}
        />
        <button
          onClick={submit}
          disabled={!draft.trim() || create.isPending}
          className="btn-primary"
        >
          <Send size={14} /> {create.isPending ? 'Posting…' : 'Post'}
        </button>
      </div>
      <div className="text-xs text-zinc-500 mt-1">Tip: ⌘/Ctrl+Enter to post.</div>
    </div>
  );
}
